import React, { Component } from "react";
import Breadcrumb from "../../common/breadcrumb";
import { DollarSign, TrendingUp, AlertTriangle } from "react-feather";
import ClipLoader from "react-spinners/ClipLoader";
import { getOrdersForProfitReport } from "../../../firebase/firebase.utils";

const QUICK_FILTERS = [
  { key: "this-month", label: "This Month" },
  { key: "last-month", label: "Last Month" },
  { key: "last-30", label: "Last 30 Days" },
  { key: "custom", label: "Custom Range" },
];

class ProfitReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      dateFilter: "this-month",
      customStartDate: "",
      customEndDate: "",
      summary: null,
    };
  }

  componentDidMount() {
    this.loadReport("this-month");
  }

  getDateRange = (filter) => {
    const now = new Date();
    if (filter === "this-month") {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { startTs: start.getTime(), endTs: now.getTime() };
    }
    if (filter === "last-month") {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
      return { startTs: start.getTime(), endTs: end.getTime() };
    }
    if (filter === "last-30") {
      return { startTs: now.getTime() - 30 * 86400000, endTs: now.getTime() };
    }
    if (filter === "custom") {
      const { customStartDate, customEndDate } = this.state;
      const startTs = customStartDate
        ? new Date(customStartDate).setHours(0, 0, 0, 0)
        : null;
      const endTs = customEndDate
        ? new Date(customEndDate).setHours(23, 59, 59, 999)
        : null;
      return { startTs, endTs };
    }
    return { startTs: null, endTs: null };
  };

  getLineSalePrice = (item) => {
    if (item.selectedVariation?.id) {
      return item.selectedVariation.salePrice > 0
        ? item.selectedVariation.salePrice
        : item.selectedVariation.price || 0;
    }
    return item.product?.salePrice > 0
      ? item.product.salePrice
      : item.product?.price || 0;
  };

  summarize = (orders) => {
    let revenue = 0;
    let pricedRevenue = 0;
    let cogs = 0;
    let unpricedCount = 0;
    let unpricedQty = 0;
    let unpricedRevenue = 0;
    const rows = [];

    orders.forEach((order) => {
      (order.orders || []).forEach((item) => {
        const qty = parseInt(item.quantity) || 0;
        const salePrice = this.getLineSalePrice(item);
        const lineRevenue = salePrice * qty;
        const hasCost =
          item.costPrice !== undefined &&
          item.costPrice !== null &&
          item.costPrice !== "";

        revenue += lineRevenue;

        if (hasCost) {
          pricedRevenue += lineRevenue;
          cogs += parseFloat(item.costPrice) || 0;
        } else {
          unpricedCount += 1;
          unpricedQty += qty;
          unpricedRevenue += lineRevenue;
        }

        rows.push({
          orderId: order.id,
          orderStatus: order.orderStatus,
          productName: item.product?.name || item.name || "—",
          qty,
          salePrice,
          lineRevenue,
          costPrice: hasCost ? parseFloat(item.costPrice) || 0 : null,
          profit: hasCost ? lineRevenue - (parseFloat(item.costPrice) || 0) : null,
        });
      });
    });

    return {
      orderCount: orders.length,
      revenue,
      pricedRevenue,
      cogs,
      grossProfit: pricedRevenue - cogs,
      unpricedCount,
      unpricedQty,
      unpricedRevenue,
      rows,
    };
  };

  loadReport = async (filter) => {
    this.setState({ loading: true, dateFilter: filter });
    const { startTs, endTs } = this.getDateRange(filter);
    if (filter === "custom" && (!startTs || !endTs)) {
      this.setState({ loading: false, summary: null });
      return;
    }
    const orders = await getOrdersForProfitReport({ startTs, endTs });
    this.setState({ summary: this.summarize(orders), loading: false });
  };

  applyCustomRange = () => {
    this.loadReport("custom");
  };

  render() {
    const { loading, dateFilter, customStartDate, customEndDate, summary } = this.state;

    const cards = summary
      ? [
          {
            label: "Revenue",
            value: `৳ ${summary.revenue.toLocaleString()}`,
            icon: <DollarSign size={18} />,
            color: "#4F46E5",
            bg: "#EEF2FF",
            border: "#A5B4FC",
          },
          {
            label: "COGS",
            value: `৳ ${summary.cogs.toLocaleString()}`,
            icon: <DollarSign size={18} />,
            color: "#D97706",
            bg: "#FFFBEB",
            border: "#FCD34D",
          },
          {
            label: "Gross Profit",
            value: `৳ ${summary.grossProfit.toLocaleString()}`,
            icon: <TrendingUp size={18} />,
            color: "#059669",
            bg: "#ECFDF5",
            border: "#6EE7B7",
          },
          {
            label: "Delivered Orders",
            value: summary.orderCount,
            icon: <TrendingUp size={18} />,
            color: "#4F46E5",
            bg: "#EEF2FF",
            border: "#A5B4FC",
          },
        ]
      : [];

    return (
      <div>
        <Breadcrumb title="Profit Report" parent="Reports" />
        <div className="container-fluid">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 20,
              flexWrap: "wrap",
            }}
          >
            {QUICK_FILTERS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => this.setState({ dateFilter: key }, () => {
                  if (key !== "custom") this.loadReport(key);
                })}
                className="btn btn-sm"
                style={{
                  background: dateFilter === key ? "linear-gradient(135deg, #4F46E5, #7C3AED)" : "#f1f5f9",
                  color: dateFilter === key ? "white" : "#475569",
                  border: "none",
                  fontSize: 12,
                  padding: "5px 14px",
                  borderRadius: 6,
                }}
              >
                {label}
              </button>
            ))}
            {dateFilter === "custom" && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => this.setState({ customStartDate: e.target.value })}
                  className="form-control form-control-sm"
                  style={{ width: 150 }}
                />
                <span style={{ color: "#888" }}>to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => this.setState({ customEndDate: e.target.value })}
                  className="form-control form-control-sm"
                  style={{ width: 150 }}
                />
                <button
                  onClick={this.applyCustomRange}
                  className="btn btn-sm"
                  style={{
                    background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                    color: "white",
                    border: "none",
                    fontSize: 12,
                    padding: "5px 14px",
                    borderRadius: 6,
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <ClipLoader size={32} color="#06A0E0" loading />
              <div style={{ marginTop: 12, color: "#888", fontSize: 13 }}>
                Crunching numbers…
              </div>
            </div>
          ) : !summary ? (
            <div style={{ textAlign: "center", padding: 60, color: "#aaa", fontSize: 14 }}>
              Pick a date range to see the report.
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
                {cards.map((card) => (
                  <div
                    key={card.label}
                    style={{
                      background: card.bg,
                      border: `1.5px solid ${card.border}`,
                      borderRadius: 10,
                      padding: "14px 20px",
                      minWidth: 170,
                      flex: "0 0 auto",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        color: card.color,
                        marginBottom: 6,
                      }}
                    >
                      {card.icon}
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        {card.label}
                      </span>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: card.color }}>
                      {card.value}
                    </div>
                  </div>
                ))}
              </div>

              {summary.unpricedCount > 0 && (
                <div
                  style={{
                    background: "#FFFBEB",
                    border: "1.5px solid #FCD34D",
                    borderRadius: 10,
                    padding: "12px 18px",
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    color: "#92400E",
                    fontSize: 13,
                  }}
                >
                  <AlertTriangle size={18} />
                  <span>
                    {summary.unpricedCount} line item(s) ({summary.unpricedQty} pc, ৳{" "}
                    {summary.unpricedRevenue.toLocaleString()} revenue) have no cost data yet —
                    they were delivered before FIFO cost tracking was enabled, or their product
                    had no purchase lots recorded at the time of sale. Excluded from COGS and
                    Gross Profit above.
                  </span>
                </div>
              )}

              <div className="card" style={{ borderRadius: 10, overflow: "hidden" }}>
                <div style={{ overflowX: "auto", maxHeight: 520, overflowY: "auto" }}>
                  <table className="table table-hover" style={{ marginBottom: 0 }}>
                    <thead>
                      <tr>
                        {["Order ID", "Status", "Product", "Qty", "Sale Price", "Revenue", "Cost", "Profit"].map((h) => (
                          <th
                            key={h}
                            style={{
                              background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                              color: "white",
                              fontWeight: 600,
                              fontSize: 12,
                              padding: "12px 14px",
                              whiteSpace: "nowrap",
                              border: "none",
                              position: "sticky",
                              top: 0,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {summary.rows.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#aaa" }}>
                            No delivered orders in this range.
                          </td>
                        </tr>
                      ) : (
                        summary.rows.map((row, i) => (
                          <tr key={`${row.orderId}-${i}`}>
                            <td style={{ padding: "10px 14px", fontSize: 12, whiteSpace: "nowrap" }}>
                              {row.orderId}
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 12, whiteSpace: "nowrap" }}>
                              {row.orderStatus}
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 13 }}>{row.productName}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13 }}>{row.qty}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, whiteSpace: "nowrap" }}>
                              ৳ {row.salePrice.toLocaleString()}
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: "#4F46E5", fontWeight: 600, whiteSpace: "nowrap" }}>
                              ৳ {row.lineRevenue.toLocaleString()}
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 13, whiteSpace: "nowrap" }}>
                              {row.costPrice === null ? (
                                <span style={{ color: "#D97706", fontStyle: "italic" }}>no data</span>
                              ) : (
                                `৳ ${row.costPrice.toLocaleString()}`
                              )}
                            </td>
                            <td
                              style={{
                                padding: "10px 14px",
                                fontSize: 13,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                color: row.profit === null ? "#aaa" : row.profit >= 0 ? "#059669" : "#DC2626",
                              }}
                            >
                              {row.profit === null ? "—" : `৳ ${row.profit.toLocaleString()}`}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default ProfitReport;
